import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { COLORS, money, tip } from "./PerfCharts";

function Stat({ label, value, tone = "text-white", testid }) {
  return (
    <div className="rounded-xl bg-[#050914] border border-blue-900/40 p-3" data-testid={testid}>
      <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function BreakdownTable({ rows, cur, title, testid }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-blue-900/40" data-testid={testid}>
      <div className="px-3 py-2 text-sm font-semibold bg-[#050914]">{title}</div>
      <table className="w-full text-sm">
        <thead className="mono text-[10px] uppercase tracking-widest text-[#64748B] bg-[#050914]">
          <tr>{["#", "Name", "Net profit", "Gross", "Swap + comm.", "Trades", "Win rate", "PF"].map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((b, i) => (
            <tr key={b.label + i} className="border-t border-blue-900/30">
              <td className="px-3 py-2 text-[#FBBF24]">{i + 1}</td>
              <td className="px-3 py-2 whitespace-nowrap">{b.label}{b.symbols?.length && !b.store_bot && !title.includes("symbol") ? <span className="text-[#64748B] text-xs"> · {b.symbols.join(", ")}</span> : null}</td>
              <td className={`px-3 py-2 font-semibold whitespace-nowrap ${b.net >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>{money(b.net, cur)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{money(b.gross, cur)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-[#94A3B8]">{money(b.costs, cur)}</td>
              <td className="px-3 py-2">{b.trades}</td>
              <td className="px-3 py-2">{b.win_rate == null ? "-" : `${b.win_rate}%`}</td>
              <td className="px-3 py-2">{b.profit_factor ?? (b.no_losses ? "no losses" : "-")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccountOverview({ data, onAccount, hideAccount }) {
  const a = data.account;
  const cur = a.currency;
  const s = data.summary;
  const pie = data.by_symbol.filter((x) => x.net > 0).map((x) => ({ name: x.label, value: x.net }));
  return (
    <div data-testid="account-overview">
      <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-[#94A3B8]">
        MT5 account
        {data.accounts.length > 1 && !hideAccount ? (
          <select value={a.account} onChange={(e) => onAccount(e.target.value)} data-testid="account-select" className="bg-[#0B1220] border border-blue-900/60 rounded px-2 py-1">
            {data.accounts.map((x) => <option key={x.account} value={x.account}>{x.account} ({x.demo ? "demo" : "live"})</option>)}
          </select>
        ) : <b className="text-white" data-testid="account-number">{a.account}</b>}
        <span>· {a.demo ? "demo" : "live"} · {a.server} · synced {a.updated_at ? new Date(a.updated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "-"}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
        <Stat testid="acct-net" label="Net profit" value={money(s.net, cur)} tone={s.net >= 0 ? "text-[#34D399]" : "text-[#F87171]"} />
        <Stat testid="acct-balance" label="Balance" value={money(a.balance, cur)} />
        <Stat testid="acct-equity" label="Equity" value={money(a.equity, cur)} />
        <Stat testid="acct-deposits" label="Deposits" value={money(data.deposits, cur)} />
        <Stat testid="acct-trades" label="Closed trades" value={s.trades} />
        <Stat testid="acct-winrate" label="Win rate · PF" value={`${s.win_rate ?? "-"}% · ${s.profit_factor ?? "-"}`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
        <div className="lg:col-span-3 rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid="acct-timeline-chart">
          <div className="text-sm font-semibold mb-2">Account profit over time</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.timeline}><CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="d" stroke="#64748B" fontSize={10} /><YAxis stroke="#64748B" fontSize={10} width={70} tickFormatter={(v) => money(v, cur)} />
              <Tooltip {...tip} formatter={(v) => money(v, cur)} />
              <Line type="linear" dataKey="total" name="Running total" stroke="#34D399" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-2 rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid="acct-symbol-pie">
          <div className="text-sm font-semibold mb-2">Profit by symbol</div>
          {pie.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2} stroke="none">
                {pie.map((p, i) => <Cell key={p.name} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip {...tip} formatter={(v) => money(v, cur)} /></PieChart>
            </ResponsiveContainer>
          ) : <div className="text-xs text-[#64748B] py-16 text-center">No profitable symbols in this period.</div>}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#94A3B8]">
            {pie.map((p, i) => <span key={p.name} className="flex items-center gap-1"><i className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.name}</span>)}
          </div>
        </div>
      </div>
      <div className="rounded-xl bg-[#050914] border border-blue-900/40 p-4 mt-4" data-testid="acct-bot-bars">
        <div className="text-sm font-semibold mb-2">Profit by bot</div>
        <ResponsiveContainer width="100%" height={Math.max(160, data.by_bot.length * 36)}>
          <BarChart data={data.by_bot} layout="vertical" margin={{ left: 10, right: 20 }}>
            <XAxis type="number" stroke="#64748B" fontSize={10} /><YAxis type="category" dataKey="label" width={210} stroke="#94A3B8" fontSize={10} />
            <Tooltip {...tip} formatter={(v) => money(v, cur)} cursor={{ fill: "#1E293B55" }} />
            <Bar dataKey="net" radius={[0, 6, 6, 0]}>{data.by_bot.map((b) => <Cell key={b.label} fill={b.net >= 0 ? "#34D399" : "#F87171"} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <BreakdownTable rows={data.by_bot} cur={cur} title="By bot (every bot that traded this account)" testid="acct-bot-table" />
      <BreakdownTable rows={data.by_symbol} cur={cur} title="By symbol" testid="acct-symbol-table" />
      <div className="text-[11px] text-[#64748B] mt-2" data-testid="acct-reconcile-note">
        Taken from your full MT5 account history, so net profit matches the "Profit" total at the bottom of MT5's History tab:
        trades + swap + commission{data.other_fees ? ` + other charges/interest (${money(data.other_fees, cur)})` : ""}. Bots that aren't from the store show as "Other bot (magic …)".
      </div>
    </div>
  );
}
